import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
} from "@angular/core";
import { fromEvent, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";

@Directive({
  selector: "[appScaleContent]",
  standalone: false,
})
export class ScaleContentDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() appScaleContent: HTMLElement | null = null;

  private destroy$ = new Subject<void>();
  private mutationObserver: MutationObserver;
  private scaleFrameId = 0;
  private isScaling = false;
  private lastScale = 1;
  private lastWidth = 0;

  constructor(private el: ElementRef<HTMLElement>) {
    this.mutationObserver = new MutationObserver(() => {
      if (this.isScaling) {
        return;
      }
      this.scheduleScale();
    });
  }

  ngOnInit() {
    fromEvent(window, "resize")
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.scheduleScale());
  }

  ngAfterViewInit() {
    this.scheduleScale();
    this.mutationObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
      characterData: false,
      attributes: false,
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.mutationObserver.disconnect();
    if (this.scaleFrameId) {
      cancelAnimationFrame(this.scaleFrameId);
    }
  }

  private scheduleScale(): void {
    if (this.scaleFrameId) {
      return;
    }
    this.scaleFrameId = requestAnimationFrame(() => {
      this.scaleFrameId = 0;
      this.scale();
    });
  }

  private scale(): void {
    const container =
      this.appScaleContent || this.el.nativeElement.parentElement;
    const content = this.el.nativeElement;

    if (!container || !content) {
      return;
    }

    this.isScaling = true;
    try {
      content.style.transform = "none";
      content.style.width = "auto";
      content.style.maxWidth = "none";

      const containerStyle = getComputedStyle(container);
      const availableWidth =
        container.clientWidth -
        parseFloat(containerStyle.paddingLeft) -
        parseFloat(containerStyle.paddingRight);

      if (availableWidth <= 0) {
        return;
      }

      const naturalWidth = content.scrollWidth || content.offsetWidth;
      if (!naturalWidth) {
        return;
      }

      const nextScale = Math.min(1, availableWidth / naturalWidth);
      if (
        Math.abs(nextScale - this.lastScale) < 0.01 &&
        Math.abs(availableWidth - this.lastWidth) < 1
      ) {
        content.style.transform = `scale(${this.lastScale})`;
        content.style.transformOrigin = "top left";
        content.style.width = `${naturalWidth}px`;
        content.style.display = "inline-block";
        content.style.maxWidth = "100%";
        return;
      }

      this.lastScale = nextScale;
      this.lastWidth = availableWidth;
      content.style.transform = `scale(${nextScale})`;
      content.style.transformOrigin = "top left";
      content.style.width = `${naturalWidth}px`;
      content.style.display = "inline-block";
      content.style.maxWidth = "100%";
    } finally {
      this.isScaling = false;
    }
  }
}
